import React, { useEffect, useState } from "react";
import { Tag, Spin, Select } from "antd";
import dayjs from "dayjs";
import Table from "../../../../core/pagination/datatable";
// import { Link } from "react-router-dom";
import { BASE_CRM_URL } from "../../../components/config/config";
import Swal from "sweetalert2";
import PropTypes from "prop-types";

const REMINDER_BADGE_COLORS = {
  pending: {
    background: "#FFF4DE",
    color: "#B26A00",
  },
  completed: {
    background: "#E8F8F0",
    color: "#198754",
  },
};

const ReminderBadgeSelectStyles = () => (
  <style>
    {Object.entries(REMINDER_BADGE_COLORS)
      .map(
        ([key, { background, color }]) => `
          .reminder-badge-${key} .ant-select-selector {
            background-color: ${background} !important;
            color: ${color} !important;
            border: none !important;
            border-radius: 20px !important;
            box-shadow: none !important;
          }

          .reminder-badge-${key} .ant-select-selection-item {
            color: ${color} !important;
            font-weight: 600;
            font-size: 12px;
          }

          .reminder-badge-${key} .ant-select-arrow {
            color: ${color} !important;
          }
        `,
      )
      .join("\n")}
  </style>
);

const StatusBadgeSelect = ({ record, onStatusUpdate }) => {
  const [loading, setLoading] = useState(false);

  const isCompleted = Number(record.isnotified) === 1;
  const colorKey = isCompleted ? "completed" : "pending";

  const handleChange = async (value) => {
    try {
      setLoading(true);

      const response = await fetch(
        `${BASE_CRM_URL}reminder/UpdateReminderNotification`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id: record.id,
            isnotified: Number(value),
          }),
        },
      );

      const result = await response.json();

      if (!response.ok || !result?.Status) {
        throw new Error(result?.Message || "Failed to update status");
      }

      // Fetch the latest reminder data again
      await onStatusUpdate();

      Swal.fire({
        icon: "success",
        title: "Status Updated",
        text: result?.Message || "Reminder status updated successfully.",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Update status error:", error);

      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: error.message || "Unable to update reminder status.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Select
      value={String(record.isnotified ?? 0)}
      className={`reminder-badge-${colorKey}`}
      style={{ minWidth: 105 }}
      size="small"
      bordered={false}
      loading={loading}
      disabled={loading}
      onChange={handleChange}
    >
      <Select.Option value="0">Pending</Select.Option>
      <Select.Option value="1">Completed</Select.Option>
    </Select>
  );
};

StatusBadgeSelect.propTypes = {
  record: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    isnotified: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onStatusUpdate: PropTypes.func.isRequired,
  }).isRequired,

  onStatusUpdate: PropTypes.func.isRequired,
  // fetchReminders: PropTypes.func.isRequired,
};

const ReminderTable = () => {
  const [reminders, setReminders] = useState([]);
  const [remindersLoading, setRemindersLoading] = useState(false);
  const [remindersError, setRemindersError] = useState(null);

  const userDetails = JSON.parse(sessionStorage.getItem("userDetails") || "{}");

  const staffId = userDetails?.staffid;

  useEffect(() => {
    fetchReminders();
  }, []);

  const fetchReminders = async () => {
    try {
      setRemindersLoading(true);
      setRemindersError(null);

      const response = await fetch(
        `${BASE_CRM_URL}reminder/GetRemindersByStaffId?staffid=${staffId}`,
      );

      if (!response.ok) {
        throw new Error("Failed to fetch reminders");
      }

      const data = await response.json();

      if (data?.Status && Array.isArray(data?.ResultData)) {
        setReminders(data.ResultData);
      } else {
        setReminders([]);
      }
    } catch (error) {
      setRemindersError(error.message);
      console.error("Error fetching reminders:", remindersError);
      setReminders([]);
    } finally {
      setRemindersLoading(false);
    }
  };

  const getRelationTag = (relType) => {
    const type = relType?.toLowerCase();

    const relationConfig = {
      estimate: {
        color: "orange",
        label: "Estimate",
      },
      invoice: {
        color: "blue",
        label: "Invoice",
      },
      lead: {
        color: "cyan",
        label: "Lead",
      },
      proposal: {
        color: "purple",
        label: "Proposal",
      },
      customer: {
        color: "green",
        label: "Customer",
      },
      creditnote: {
        color: "red",
        label: "Credit Note",
      },
      expense: {
        color: "gold",
        label: "Expense",
      },
    };

    const config = relationConfig[type];

    return (
      <Tag color={config?.color || "default"}>
        {config?.label || relType || "N/A"}
      </Tag>
    );
  };

  const columns = [
    {
      title: "Relation Type",
      dataIndex: "rel_type",
      key: "rel_type",
      width: 150,
      render: (relType) => getRelationTag(relType),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "isnotified",
      key: "isnotified",
      width: 130,
      align: "center",
      render: (_, record) => (
        <StatusBadgeSelect record={record} onStatusUpdate={fetchReminders} />
      ),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 180,
      render: (date) =>
        date ? dayjs(date).format("DD/MM/YYYY hh:mm A") : "N/A",
    },
  ];

  return (
    <div className="card table-list-card">
      <ReminderBadgeSelectStyles />
      <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
        <div className="search-set p-3"></div>
        <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3"></div>
      </div>
      <div className="card-body">
        <div className="table-responsive">
          {remindersLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "250px", // adjust height if needed
              }}
            >
              <Spin size="large" />
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={reminders}
              key={`reminders-${reminders.map((item) => item.id).join("-")}`}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ReminderTable;
